import React, { useState, useMemo } from 'react';
import ControlLimpiezaBanos from './ControlLimpiezaBanos';
import ManejoResiduosSST from './ManejoResiduosSST'; 
import FormatoAccionCorrectiva from './FormatoAccionCorrectiva';
import FormatoLimpiezaDesinfeccion from './FormatoLimpiezaDesinfeccion'; 
import FormatoPracticasHigienicas from './FormatoPracticasHigienicas'; 
import FormatoControlPlagas from './FormatoControlPlagas';
import FormatoControlTemperaturas from './FormatoControlTemperaturas'; 
import FormatoAguaPotable from './FormatoAguaPotable'; 

export default function SubMenuFormatosSST({ usuario, onBack }) {
  const [vistaInterna, setVistaInterna] = useState('menu_interno'); 

  // =====================================================================
  // 1. LÓGICA DE CONTROL ABAC BLINDADA
  // =====================================================================
  const permisosABAC = useMemo(() => {
    try {
      const parsed = typeof usuario?.permisos_avanzados === 'string' 
        ? JSON.parse(usuario.permisos_avanzados) 
        : (usuario?.permisos_avanzados || {});
      return parsed.micros || parsed; 
    } catch (e) { 
      return {}; 
    }
  }, [usuario]);

  // Regla Super Admin estricta
  const cargoUpper = String(usuario?.cargo || "").toUpperCase().trim();
  const esAdminGlobal = cargoUpper === "SUPER_ADMIN";
  
  // Verificador Estricto de Tarjetas
  const checkPermiso = (nombreSubmodulo) => {
    if (esAdminGlobal) return true;
    return permisosABAC[nombreSubmodulo] !== undefined;
  };

  // Evaluamos qué tarjetas debe ver el usuario
  const verPracticas = checkPermiso('Prácticas Higiénicas');
  const verTemperaturas = checkPermiso('Control de Temperaturas');
  const verPlagas = checkPermiso('Control de Plagas');
  const verLimpiezaDesinf = checkPermiso('Limpieza & Desinfección');
  const verLimpiezaBanos = checkPermiso('Limpieza de Baños');
  const verResiduos = checkPermiso('Manejo de Residuos');
  const verRechazos = checkPermiso('Rechazos & Acciones');
  const verAgua = checkPermiso('Programa Agua Potable');

  // ==========================================
  // ENRUTADOR DEL SUBMENÚ (Pasando el filtro de sede a los hijos)
  // ==========================================
  if (vistaInterna === 'limpieza_banos') {
    return <ControlLimpiezaBanos usuario={usuario} filtroABAC={permisosABAC['Limpieza de Baños']} onBack={() => setVistaInterna('menu_interno')} />;
  }

  if (vistaInterna === 'residuos_solidos') {
    return <ManejoResiduosSST usuario={usuario} filtroABAC={permisosABAC['Manejo de Residuos']} onBack={() => setVistaInterna('menu_interno')} />;
  }

  if (vistaInterna === 'accion_correctiva') {
    return <FormatoAccionCorrectiva usuario={usuario} filtroABAC={permisosABAC['Rechazos & Acciones']} onBack={() => setVistaInterna('menu_interno')} />;
  }

  if (vistaInterna === 'limpieza_desinfeccion') {
    return <FormatoLimpiezaDesinfeccion usuario={usuario} filtroABAC={permisosABAC['Limpieza & Desinfección']} onBack={() => setVistaInterna('menu_interno')} />;
  }

  if (vistaInterna === 'practicas_higienicas') {
    return <FormatoPracticasHigienicas usuario={usuario} filtroABAC={permisosABAC['Prácticas Higiénicas']} onBack={() => setVistaInterna('menu_interno')} />;
  }

  if (vistaInterna === 'control_plagas') {
    return <FormatoControlPlagas usuario={usuario} filtroABAC={permisosABAC['Control de Plagas']} onBack={() => setVistaInterna('menu_interno')} />;
  }

  if (vistaInterna === 'control_temperaturas') {
    return <FormatoControlTemperaturas usuario={usuario} filtroABAC={permisosABAC['Control de Temperaturas']} onBack={() => setVistaInterna('menu_interno')} />;
  }

  if (vistaInterna === 'agua_potable') {
    return <FormatoAguaPotable usuario={usuario} filtroABAC={permisosABAC['Programa Agua Potable']} onBack={() => setVistaInterna('menu_interno')} />;
  }

  const sinFormatos = !verPracticas && !verTemperaturas && !verPlagas && !verLimpiezaDesinf && !verLimpiezaBanos && !verResiduos && !verRechazos && !verAgua;

  // ==========================================
  // VISTA DE LA CARPETA (TARJETAS)
  // ==========================================
  return (
    <div className="bg-white/20 backdrop-blur-3xl rounded-2xl sm:rounded-[34px] shadow-2xl max-w-6xl w-full mx-auto border border-white/30 animate-fade-in relative overflow-hidden flex flex-col p-6 sm:p-10 min-h-[70vh]">
      
      {/* CABECERA DEL SUBMENÚ */}
      <div className="flex items-center gap-4 mb-10 pb-6 border-b border-white/20">
        <button 
          onClick={onBack}
          className="w-12 h-12 flex-none bg-white/60 hover:bg-white/80 text-slate-700 hover:text-red-500 flex items-center justify-center rounded-full transition-all shadow-sm border border-white/40 cursor-pointer backdrop-blur-sm"
          title="Volver al Menú Principal SST"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7"></path></svg>
        </button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-800 tracking-tight">Formatos Adicionales SST</h1>
          <p className="text-sm font-bold text-slate-600 mt-1">Selecciona el reporte o permiso que deseas diligenciar</p>
        </div>
      </div>

      {sinFormatos ? (
        <div className="p-10 text-center bg-white/40 backdrop-blur-md border border-white/50 rounded-3xl shadow-sm my-auto">
          <span className="text-4xl block mb-4">🛡️</span>
          <h3 className="text-lg font-black text-slate-800">Carpeta Vacía</h3>
          <p className="text-xs text-slate-600 font-medium mt-2">No tienes formatos adicionales asignados en tu matriz ABAC.</p>
        </div>
      ) : (
        /* GRID DE OPCIONES DE FORMULARIOS EXTRAS */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          
          {/* TARJETA 1: PRÁCTICAS HIGIÉNICAS */}
          {verPracticas && (
            <div 
              onClick={() => setVistaInterna('practicas_higienicas')}
              className="bg-white/50 hover:bg-white/80 backdrop-blur-md rounded-3xl p-6 border border-white/60 shadow-lg hover:shadow-xl transition-all cursor-pointer group flex flex-col items-center text-center transform hover:-translate-y-1"
            >
              <div className="w-20 h-20 bg-fuchsia-100/80 rounded-2xl flex items-center justify-center text-4xl shadow-inner border border-fuchsia-200 mb-4 group-hover:scale-110 transition-transform">🧴</div>
              <span className="bg-fuchsia-600 text-white text-[10px] font-black px-2.5 py-0.5 rounded-md uppercase tracking-wider mb-3">FT-CD-01</span>
              <h3 className="text-lg font-black text-slate-800 mb-2 leading-tight">Prácticas Higiénicas</h3>
              <p className="text-xs text-slate-600 font-medium">Evaluación diaria de higiene y medidas de protección por empleado.</p>
            </div>
          )}

          {/* TARJETA 2: CONTROL DE TEMPERATURAS */}
          {verTemperaturas && (
            <div 
              onClick={() => setVistaInterna('control_temperaturas')}
              className="bg-white/50 hover:bg-white/80 backdrop-blur-md rounded-3xl p-6 border border-white/60 shadow-lg hover:shadow-xl transition-all cursor-pointer group flex flex-col items-center text-center transform hover:-translate-y-1"
            >
              <div className="w-20 h-20 bg-cyan-100/80 rounded-2xl flex items-center justify-center text-4xl shadow-inner border border-cyan-200 mb-4 group-hover:scale-110 transition-transform">❄️</div>
              <span className="bg-cyan-600 text-white text-[10px] font-black px-2.5 py-0.5 rounded-md uppercase tracking-wider mb-3">FT-CD-02</span>
              <h3 className="text-lg font-black text-slate-800 mb-2 leading-tight">Control de Temperaturas</h3>
              <p className="text-xs text-slate-600 font-medium">Registro inteligente por turnos de refrigeración, congelación y calor.</p>
            </div>
          )}

          {/* TARJETA 3: CONTROL DE PLAGAS */}
          {verPlagas && (
            <div 
              onClick={() => setVistaInterna('control_plagas')}
              className="bg-white/50 hover:bg-white/80 backdrop-blur-md rounded-3xl p-6 border border-white/60 shadow-lg hover:shadow-xl transition-all cursor-pointer group flex flex-col items-center text-center transform hover:-translate-y-1"
            >
              <div className="w-20 h-20 bg-violet-100/80 rounded-2xl flex items-center justify-center text-4xl shadow-inner border border-violet-200 mb-4 group-hover:scale-110 transition-transform">🐀</div>
              <span className="bg-violet-600 text-white text-[10px] font-black px-2.5 py-0.5 rounded-md uppercase tracking-wider mb-3">FT-CD-05</span>
              <h3 className="text-lg font-black text-slate-800 mb-2 leading-tight">Control de Plagas</h3>
              <p className="text-xs text-slate-600 font-medium">Registro diario de inspección y control de plagas por turno.</p>
            </div>
          )}

          {/* TARJETA 4: LIMPIEZA Y DESINFECCIÓN */}
          {verLimpiezaDesinf && (
            <div 
              onClick={() => setVistaInterna('limpieza_desinfeccion')}
              className="bg-white/50 hover:bg-white/80 backdrop-blur-md rounded-3xl p-6 border border-white/60 shadow-lg hover:shadow-xl transition-all cursor-pointer group flex flex-col items-center text-center transform hover:-translate-y-1"
            >
              <div className="w-20 h-20 bg-blue-100/80 rounded-2xl flex items-center justify-center text-4xl shadow-inner border border-blue-200 mb-4 group-hover:scale-110 transition-transform">✨</div>
              <span className="bg-blue-600 text-white text-[10px] font-black px-2.5 py-0.5 rounded-md uppercase tracking-wider mb-3">FT-SST-08</span>
              <h3 className="text-lg font-black text-slate-800 mb-2 leading-tight">Limpieza & Desinfección</h3>
              <p className="text-xs text-slate-600 font-medium">Checklist diario global y trazabilidad mensual de todas las áreas.</p>
            </div>
          )}

          {/* TARJETA 5: LIMPIEZA DE BAÑOS */}
          {verLimpiezaBanos && (
            <div 
              onClick={() => setVistaInterna('limpieza_banos')}
              className="bg-white/50 hover:bg-white/80 backdrop-blur-md rounded-3xl p-6 border border-white/60 shadow-lg hover:shadow-xl transition-all cursor-pointer group flex flex-col items-center text-center transform hover:-translate-y-1"
            >
              <div className="w-20 h-20 bg-teal-100/80 rounded-2xl flex items-center justify-center text-4xl shadow-inner border border-teal-200 mb-4 group-hover:scale-110 transition-transform">🧽</div>
              <span className="bg-teal-600 text-white text-[10px] font-black px-2.5 py-0.5 rounded-md uppercase tracking-wider mb-3">FT-CD-03</span>
              <h3 className="text-lg font-black text-slate-800 mb-2 leading-tight">Limpieza de Baños</h3>
              <p className="text-xs text-slate-600 font-medium">Control de limpieza, desinfección y dotación en áreas sanitarias.</p>
            </div>
          )}

          {/* TARJETA 6: MANEJO DE RESIDUOS */}
          {verResiduos && (
            <div 
              onClick={() => setVistaInterna('residuos_solidos')}
              className="bg-white/50 hover:bg-white/80 backdrop-blur-md rounded-3xl p-6 border border-white/60 shadow-lg hover:shadow-xl transition-all cursor-pointer group flex flex-col items-center text-center transform hover:-translate-y-1"
            >
              <div className="w-20 h-20 bg-emerald-100/80 rounded-2xl flex items-center justify-center text-4xl shadow-inner border border-emerald-200 mb-4 group-hover:scale-110 transition-transform">♻️</div>
              <span className="bg-emerald-600 text-white text-[10px] font-black px-2.5 py-0.5 rounded-md uppercase tracking-wider mb-3">FT-SST-09</span>
              <h3 className="text-lg font-black text-slate-800 mb-2 leading-tight">Manejo de Residuos</h3>
              <p className="text-xs text-slate-600 font-medium">Registro diario de generación de residuos sólidos (Check-in).</p>
            </div>
          )}

          {/* TARJETA 7: ACCIÓN CORRECTIVA Y RECHAZOS */}
          {verRechazos && (
            <div 
              onClick={() => setVistaInterna('accion_correctiva')}
              className="bg-white/50 hover:bg-white/80 backdrop-blur-md rounded-3xl p-6 border border-white/60 shadow-lg hover:shadow-xl transition-all cursor-pointer group flex flex-col items-center text-center transform hover:-translate-y-1"
            >
              <div className="w-20 h-20 bg-rose-100/80 rounded-2xl flex items-center justify-center text-4xl shadow-inner border border-rose-200 mb-4 group-hover:scale-110 transition-transform">⛔</div>
              <span className="bg-rose-600 text-white text-[10px] font-black px-2.5 py-0.5 rounded-md uppercase tracking-wider mb-3">FR-SST-01</span>
              <h3 className="text-lg font-black text-slate-800 mb-2 leading-tight">Rechazos & Acciones</h3>
              <p className="text-xs text-slate-600 font-medium">Control de aceptación, rechazo y planes de acción de materias primas.</p>
            </div>
          )}

          {/* TARJETA 8: PROGRAMA AGUA POTABLE */}
          {verAgua && (
            <div 
              onClick={() => setVistaInterna('agua_potable')}
              className="bg-white/50 hover:bg-white/80 backdrop-blur-md rounded-3xl p-6 border border-white/60 shadow-lg hover:shadow-xl transition-all cursor-pointer group flex flex-col items-center text-center transform hover:-translate-y-1"
            >
              <div className="w-20 h-20 bg-sky-100/80 rounded-2xl flex items-center justify-center text-4xl shadow-inner border border-sky-200 mb-4 group-hover:scale-110 transition-transform">💧</div>
              <span className="bg-sky-600 text-white text-[10px] font-black px-2.5 py-0.5 rounded-md uppercase tracking-wider mb-3">FT-CD-08</span>
              <h3 className="text-lg font-black text-slate-800 mb-2 leading-tight">Programa Agua Potable</h3>
              <p className="text-xs text-slate-600 font-medium">Control mensual de calidad del agua y reportes de mantenimiento.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}